import { FC, useState } from "react";

import { Box, GlobalStyles, BoxProps, Stack } from "@mui/material";

import { useElementSize } from "@/common/hooks/useElementSize";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { ChevronLeft, ChevronRight } from "@/icons/outlined";

const DEFAULT_SNACKBAR_BOTTOM_OFFSET = 74;
const SNACKBAR_GAP_ABOVE_FOOTER = 5;

export type NextButtonLabel = "next" | "skip" | "continueToDashboard";

export interface LiveEditorActionFooterProps extends BoxProps {
  onBack?: () => void;
  backButtonLabel?: string;
  onNext?: () => void;
  disableBack?: boolean;
  disableNext?: boolean;
  nextButtonLabel?: NextButtonLabel;
  customNextButtonLabel?: string;
  isStepChanging: boolean;
  onNavigationStart?: () => void;
  additionalRightButton?: React.ReactNode;
  disableNextTooltip?: string;
  topSlot?: React.ReactNode;
  // Set by flows whose Back/Next already log named events, so autocapture doesn't record the same clicks.
  skipAutocapture?: boolean;
}

export const LiveEditorActionFooter: FC<LiveEditorActionFooterProps> = ({
  onBack,
  backButtonLabel,
  onNext,
  disableBack = false,
  disableNext = false,
  nextButtonLabel = "next",
  customNextButtonLabel,
  isStepChanging,
  onNavigationStart,
  sx: footerSx,
  additionalRightButton,
  disableNextTooltip,
  topSlot,
  skipAutocapture,
  ...restBoxProps
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();

  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isBackLoading, setIsBackLoading] = useState(false);
  const [footerElement, setFooterElement] = useState<HTMLDivElement | null>(null);
  const { height: footerHeight } = useElementSize(footerElement);
  const snackbarBottomOffset = footerHeight ? footerHeight + SNACKBAR_GAP_ABOVE_FOOTER : DEFAULT_SNACKBAR_BOTTOM_OFFSET;

  const handleNext = async () => {
    setIsNextLoading(true);
    onNavigationStart?.();
    try {
      await onNext?.();
    } finally {
      setIsNextLoading(false);
    }
  };

  const handleBack = async () => {
    if (!onBack) {
      return;
    }
    setIsBackLoading(true);
    onNavigationStart?.();
    try {
      await onBack();
    } finally {
      setIsBackLoading(false);
    }
  };

  const backButton = onBack ? (
    <DashboardButton
      vibe="neutral"
      data-test="back-button"
      data-amp-skip-autocapture={skipAutocapture || undefined}
      variant="outlined"
      onClick={handleBack}
      startIcon={<ChevronLeft />}
      disabled={(disableBack || isStepChanging) && !isBackLoading}
      isLoading={isBackLoading}
      size={isSmallScreen ? "large" : "medium"}
    >
      {backButtonLabel || t("dashboard", "common.back")}
    </DashboardButton>
  ) : null;

  const nextButton = (onClick: () => void | Promise<void>) => (
    <DashboardButton
      type="submit"
      variant="filled"
      onClick={onClick}
      endIcon={<ChevronRight />}
      disabled={(disableNext || isStepChanging) && !isNextLoading}
      isLoading={isNextLoading}
      data-test="next-button"
      data-amp-skip-autocapture={skipAutocapture || undefined}
      size={isSmallScreen ? "large" : "medium"}
    >
      {customNextButtonLabel
        ? customNextButtonLabel
        : (() => {
            switch (nextButtonLabel) {
              case "next":
                return t("common", "next");
              case "skip":
                return t("common", "skipForNow");
              case "continueToDashboard":
                return t("common", "continueToDashboard");
              default:
                return t("common", "next");
            }
          })()}
    </DashboardButton>
  );

  return (
    <>
      <Box
        ref={setFooterElement}
        display="flex"
        justifyContent="space-between"
        flexWrap={topSlot ? "wrap" : undefined}
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.border.brand.quiet}`,
          paddingX: { xs: 3, md: 4, lg: 6 },
          paddingY: 2,
          backgroundColor: "background.paper",
          ...(footerSx || {}),
        }}
        {...restBoxProps}
        data-test="step-action-footer"
      >
        {topSlot && (
          <Box width="100%" marginBottom={2}>
            {topSlot}
          </Box>
        )}
        {/* Back Button */}
        {backButton}

        <Stack direction="row" spacing={2} sx={{ ml: "auto" }}>
          {additionalRightButton}
          {onNext &&
            (disableNextTooltip ? (
              <DashboardTooltip title={disableNextTooltip}>
                <Box>{nextButton(handleNext)}</Box>
              </DashboardTooltip>
            ) : (
              nextButton(handleNext)
            ))}
        </Stack>
      </Box>

      {/* Move the snackbar up so it appears above the action footer */}
      <GlobalStyles
        styles={{
          ".notistack-SnackbarContainer": {
            bottom: `${snackbarBottomOffset}px !important`,
          },
        }}
      />
    </>
  );
};
